import React from 'react';
import { useIntl } from 'umi';
import { Button, Popconfirm, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import classNames from 'classnames';

import { INTL_ACTION, INTL_ENTITY, getIntlTranslation } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import { deleteSkillRequest } from '@/services/api/skill';
import type { SkillModel } from '@/typings/models/Skill';
import styles from './index.module.less';

type SkillRowProps = {
  skill: SkillModel;
  /** Without it the row is read-only: no edit on click, no delete. */
  onEdit?: (skillId: string) => void;
  onDeleted: () => void;
};

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

const SkillRow: React.FC<SkillRowProps> = ({ skill, onEdit, onDeleted }) => {
  const intl = useIntl();
  const editable = !!onEdit;

  const handleDelete = async () => {
    const response = await deleteSkillRequest(skill.id);
    processAPIResponseNow(response, {
      successMessage: getIntlTranslation(intl, { entity: INTL_ENTITY.SKILL, action: INTL_ACTION.REMOVE_SUCCESS }),
      onSuccess: onDeleted,
    });
  };

  return (
    <li
      className={classNames(styles.row, editable && styles.editableRow)}
      onClick={editable ? () => onEdit(skill.id) : undefined}
      onKeyDown={editable ? (event) => event.key === 'Enter' && event.target === event.currentTarget && onEdit(skill.id) : undefined}
      tabIndex={editable ? 0 : undefined}
    >
      <span className={styles.rowLabel}>{skill.label}</span>
      <Typography.Text code copyable className={styles.slug}>
        {skill.slug}
      </Typography.Text>
      {editable && (
        // The confirm popup is portalled but still bubbles through React — keep its clicks off the row.
        <span onClick={stopPropagation}>
          <Popconfirm
            title={getIntlTranslation(intl, { entity: INTL_ENTITY.SKILL, action: INTL_ACTION.REMOVE_CONFIRMATION })}
            onConfirm={handleDelete}
            placement="topRight"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              aria-label={getIntlTranslation(intl, { entity: INTL_ENTITY.SKILL, action: INTL_ACTION.REMOVE })}
            />
          </Popconfirm>
        </span>
      )}
    </li>
  );
};

export default React.memo(SkillRow);
